import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  SparklesIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ShareIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { Card, Button, Loading, Modal } from '../components/common';
import ATSScore from '../components/ATSScore';
import BeforeAfterView from '../components/BeforeAfterView';
import SkillGapReport from '../components/SkillGapReport';
import InterviewPrepCard from '../components/InterviewPrepCard';
import DomainMismatchModal from '../components/DomainMismatchModal';
import { getJob } from '../services/jobService';
import { getResumes, getResume } from '../services/resumeService';
import { tailorResume, generateCoverLetter, getTailoredResumeByJob, analyzeDomainMatch } from '../services/generateService';
import { exportResumePDF, exportCoverLetterPDF } from '../services/exportService';

const JobDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [tailoredData, setTailoredData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tailoring, setTailoring] = useState(false);
  const [generatingCover, setGeneratingCover] = useState(false);
  const [selectedResumeId, setSelectedResumeId] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloadingResume, setDownloadingResume] = useState(false);
  const [downloadingCover, setDownloadingCover] = useState(false);
  const [showBeforeAfter, setShowBeforeAfter] = useState(false);
  const [originalResume, setOriginalResume] = useState(null);
  const [animateScore, setAnimateScore] = useState(false);

  // Domain mismatch handling
  const [domainAnalysis, setDomainAnalysis] = useState(null);
  const [showMismatchModal, setShowMismatchModal] = useState(false);
  const [analyzingDomain, setAnalyzingDomain] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [jobRes, resumesRes] = await Promise.all([
        getJob(id),
        getResumes()
      ]);

      if (jobRes.success) {
        setJob(jobRes.data.job);
      }

      if (resumesRes.success) {
        setResumes(resumesRes.data.resumes);
        const defaultResume = resumesRes.data.resumes.find(r => r.isDefault);
        if (defaultResume) {
          setSelectedResumeId(defaultResume.id);
        } else if (resumesRes.data.resumes.length > 0) {
          setSelectedResumeId(resumesRes.data.resumes[0].id);
        }
      }

      // Try to get existing tailored resume
      try {
        const tailoredRes = await getTailoredResumeByJob(id);
        if (tailoredRes.success) {
          setTailoredData(tailoredRes.data);
        }
      } catch (err) {
        // No tailored resume yet - that's fine
      }
    } catch (err) {
      toast.error('Failed to load job details');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleTailor = async () => {
    if (!selectedResumeId) {
      toast.error('Please select a resume first');
      return;
    }

    // Step 1: Analyze domain match first
    setAnalyzingDomain(true);
    try {
      const analysisRes = await analyzeDomainMatch(selectedResumeId, id);

      if (analysisRes.success) {
        const analysis = analysisRes.data.domainAnalysis;
        setDomainAnalysis(analysis);

        // If low match, show warning modal for user to choose
        if (analysis.matchLevel === 'Low') {
          setAnalyzingDomain(false);
          setShowMismatchModal(true);
          return; // Wait for user choice in modal
        }

        // If medium match, show toast warning but proceed
        if (analysis.matchLevel === 'Medium') {
          toast('This job is partially outside your primary domain. Results may vary.', {
            icon: '⚠️',
            duration: 4000
          });
        }
      }
    } catch (err) {
      // If analysis fails, proceed with tailoring anyway
      console.error('Domain analysis failed:', err);
    }
    setAnalyzingDomain(false);

    // Step 2: Proceed with standard tailoring
    await proceedWithTailoring('standard');
  };

  // Proceed with tailoring (called after user choice or for good matches)
  const proceedWithTailoring = async (mode) => {
    setTailoring(true);
    setAnimateScore(false);
    setShowMismatchModal(false);

    try {
      // Fetch original resume data for before/after comparison
      const originalRes = await getResume(selectedResumeId);
      if (originalRes.success) {
        setOriginalResume(originalRes.data.resume);
      }

      const response = await tailorResume(selectedResumeId, id, {
        tailoringMode: mode,
        domainAnalysisData: domainAnalysis
      });

      if (response.success) {
        setTailoredData(response.data);
        const { track } = await import('../utils/analytics').then(m => ({ track: m.analytics.track }));
        track('resume_tailored', { mode, score: response.data?.atsScore });
        // Trigger score animation
        setTimeout(() => setAnimateScore(true), 100);

        // Different success messages based on mode
        if (mode === 'career_pivot') {
          toast.success('Resume tailored with career pivot strategy!');
        } else {
          toast.success('Resume tailored successfully!');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to tailor resume');
    } finally {
      setTailoring(false);
    }
  };

  const handleGenerateCoverLetter = async () => {
    setGeneratingCover(true);
    try {
      const response = await generateCoverLetter(id, selectedResumeId);
      if (response.success) {
        setTailoredData(prev => ({
          ...prev,
          tailoredResume: {
            ...prev?.tailoredResume,
            coverLetter: response.data.coverLetter
          }
        }));
        toast.success('Cover letter generated!');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate cover letter');
    } finally {
      setGeneratingCover(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadResume = async () => {
    if (!tailoredResume?._id) return;

    setDownloadingResume(true);
    try {
      const filename = `resume-${job.company?.replace(/\s+/g, '-') || 'tailored'}.pdf`;
      await exportResumePDF(tailoredResume._id, filename);
      toast.success('Resume downloaded!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to download resume');
    } finally {
      setDownloadingResume(false);
    }
  };

  const handleDownloadCoverLetter = async () => {
    if (!tailoredResume?._id || !tailoredResume.coverLetter) return;

    setDownloadingCover(true);
    try {
      const filename = `cover-letter-${job.company?.replace(/\s+/g, '-') || 'letter'}.pdf`;
      await exportCoverLetterPDF(tailoredResume._id, filename);
      toast.success('Cover letter downloaded!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to download cover letter');
    } finally {
      setDownloadingCover(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loading size="lg" text="Loading job details..." />
      </div>
    );
  }

  if (!job) {
    return null;
  }

  const tailoredResume = tailoredData?.tailoredResume;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate('/jobs')}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 mt-1"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{job.title}</h1>
            <p className="text-lg text-gray-600">{job.company}</p>
            {job.location && (
              <p className="text-sm text-gray-500 mt-1">{job.location}</p>
            )}
          </div>
        </div>

        {job.url && (
          <a
            href={job.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:text-primary-700 text-sm"
          >
            View Original →
          </a>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tailoring action */}
          <Card className="bg-gradient-to-r from-primary-50 to-primary-100 border-primary-200">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary-600 rounded-lg">
                  <SparklesIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary-900">
                    {tailoredResume ? 'Resume Tailored!' : 'Tailor Your Resume'}
                  </h3>
                  <p className="text-sm text-primary-700 mt-1">
                    {tailoredResume
                      ? `ATS Score: ${tailoredResume.atsScore}%`
                      : 'AI will optimize your resume for this specific job'}
                  </p>
                </div>
              </div>

              {!tailoredResume ? (
                <Button onClick={() => setShowResumeModal(true)} loading={tailoring}>
                  <SparklesIcon className="h-5 w-5 mr-2" />
                  Tailor Resume
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button onClick={handleDownloadResume} loading={downloadingResume}>
                    <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                    Download PDF
                  </Button>
                  <Button variant="secondary" onClick={handleTailor} loading={tailoring}>
                    Re-tailor
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {/* Tailored content */}
          {tailoredResume && (
            <>
              {/* ATS Score - The "Holy Shit" Moment */}
              <Card
                title="ATS Compatibility Score"
                actions={
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowBeforeAfter(!showBeforeAfter)}
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    {showBeforeAfter ? 'Hide' : 'Show'} Before/After
                  </Button>
                }
              >
                <div className="flex flex-col items-center py-4">
                  <ATSScore
                    score={tailoredResume.atsScore}
                    previousScore={originalResume ? 65 : null} // Estimate original score
                    animate={animateScore}
                    size="lg"
                    domainMatch={tailoredResume.domainMatch}
                  />
                </div>
              </Card>

              {/* Domain Match Context Card - shown for career pivots */}
              {tailoredResume.domainMatch?.level === 'Low' && (
                <Card className="bg-amber-50 border-amber-200">
                  <div className="flex items-start gap-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">Career Pivot Context</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        {tailoredResume.domainMatch.explanation}
                      </p>
                      {tailoredResume.tailoringMode === 'career_pivot' && tailoredResume.careerPivotData?.honestAssessment && (
                        <p className="text-sm text-amber-600 mt-2 italic">
                          {tailoredResume.careerPivotData.honestAssessment}
                        </p>
                      )}
                      <p className="text-xs text-amber-600 mt-2">
                        ATS scores for career pivots are typically lower. Consider direct outreach to hiring managers.
                      </p>
                    </div>
                  </div>
                </Card>
              )}

              {/* Career Pivot Narrative - shown when career pivot mode was used */}
              {tailoredResume.tailoringMode === 'career_pivot' && tailoredResume.tailoredContent?.careerPivotNarrative && (
                <Card title="Career Change Narrative">
                  <p className="text-gray-700 leading-relaxed italic">
                    "{tailoredResume.tailoredContent.careerPivotNarrative}"
                  </p>
                  <p className="text-xs text-gray-500 mt-3">
                    Use this narrative in your cover letter or when reaching out directly.
                  </p>
                </Card>
              )}

              {/* Before/After Comparison View */}
              {showBeforeAfter && originalResume && (
                <Card title="Resume Transformation">
                  <BeforeAfterView
                    originalResume={originalResume}
                    tailoredResume={tailoredResume}
                    keywordAnalysis={tailoredResume.keywordAnalysis}
                    atsScoreOriginal={65} // Estimated original
                    atsScoreTailored={tailoredResume.atsScore}
                  />
                </Card>
              )}

              {/* Skill Gap Report - Prominent missing skills analysis */}
              <SkillGapReport
                missingKeywords={tailoredResume.keywordAnalysis?.missing}
                matchedKeywords={tailoredResume.keywordAnalysis?.matched}
                jobTitle={job.title}
                company={job.company}
              />

              {/* Tailored Summary */}
              {tailoredResume.tailoredContent?.summary && (
                <Card title="Tailored Summary">
                  <p className="text-gray-700 leading-relaxed">
                    {tailoredResume.tailoredContent.summary}
                  </p>
                </Card>
              )}

              {/* Cover Letter */}
              <Card
                title="Cover Letter"
                actions={
                  tailoredResume.coverLetter ? (
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(tailoredResume.coverLetter)}
                      >
                        {copied ? (
                          <CheckIcon className="h-4 w-4 mr-1" />
                        ) : (
                          <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                        )}
                        Copy
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDownloadCoverLetter}
                        loading={downloadingCover}
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                    </div>
                  ) : null
                }
              >
                {tailoredResume.coverLetter ? (
                  <pre className="whitespace-pre-wrap font-sans text-gray-700 text-sm leading-relaxed">
                    {tailoredResume.coverLetter}
                  </pre>
                ) : (
                  <div className="text-center py-6">
                    <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-4">No cover letter generated yet</p>
                    <Button onClick={handleGenerateCoverLetter} loading={generatingCover}>
                      <SparklesIcon className="h-5 w-5 mr-2" />
                      Generate Cover Letter
                    </Button>
                  </div>
                )}
              </Card>

              {/* Suggestions */}
              {tailoredResume.suggestions?.length > 0 && (
                <Card title="Improvement Suggestions">
                  <ul className="space-y-2">
                    {tailoredResume.suggestions.map((suggestion, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700">
                        <span className="text-primary-600 mt-1">•</span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {/* Interview Prep */}
              <InterviewPrepCard
                jobId={id}
                resumeId={selectedResumeId}
                jobTitle={job.title}
                company={job.company}
              />
            </>
          )}

          {/* Job Description */}
          {job.description && (
            <Card title="Job Description">
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-gray-700">
                  {job.description}
                </pre>
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Job Requirements */}
          {job.extractedData && (
            <Card title="Requirements">
              {job.extractedData.mustHave?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Must Have</h4>
                  <ul className="space-y-1">
                    {job.extractedData.mustHave.map((req, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-red-500">•</span>
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {job.extractedData.niceToHave?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Nice to Have</h4>
                  <ul className="space-y-1">
                    {job.extractedData.niceToHave.map((req, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-yellow-500">•</span>
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {job.extractedData.skills?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Skills</h4>
                  <div className="flex flex-wrap gap-1">
                    {job.extractedData.skills.map((skill, i) => (
                      <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Job Details */}
          <Card title="Details">
            <dl className="space-y-3 text-sm">
              {job.extractedData?.yearsExperience && (
                <div>
                  <dt className="text-gray-500">Experience</dt>
                  <dd className="font-medium text-gray-900">{job.extractedData.yearsExperience}</dd>
                </div>
              )}
              {job.extractedData?.remote && job.extractedData.remote !== 'unknown' && (
                <div>
                  <dt className="text-gray-500">Work Type</dt>
                  <dd className="font-medium text-gray-900 capitalize">{job.extractedData.remote}</dd>
                </div>
              )}
              {job.extractedData?.salary?.min && (
                <div>
                  <dt className="text-gray-500">Salary Range</dt>
                  <dd className="font-medium text-gray-900">
                    ${job.extractedData.salary.min.toLocaleString()}
                    {job.extractedData.salary.max && ` - $${job.extractedData.salary.max.toLocaleString()}`}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500">Captured</dt>
                <dd className="font-medium text-gray-900">
                  {new Date(job.capturedAt).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>

      {/* Domain Mismatch Modal */}
      <DomainMismatchModal
        isOpen={showMismatchModal}
        onClose={() => setShowMismatchModal(false)}
        domainAnalysis={domainAnalysis}
        onProceedStandard={() => proceedWithTailoring('standard')}
        onProceedCareerPivot={() => proceedWithTailoring('career_pivot')}
        jobTitle={job.title}
        company={job.company}
        loading={tailoring}
      />

      {/* Resume selection modal */}
      <Modal
        isOpen={showResumeModal}
        onClose={() => setShowResumeModal(false)}
        title="Select Resume to Tailor"
        size="md"
      >
        {resumes.length === 0 ? (
          <div className="text-center py-6">
            <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">No resumes uploaded yet</p>
            <Button onClick={() => navigate('/resumes/new')}>
              Upload Resume
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {resumes.map((resume) => (
              <div
                key={resume.id}
                onClick={() => setSelectedResumeId(resume.id)}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedResumeId === resume.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">{resume.name}</h4>
                    <p className="text-sm text-gray-500">
                      {resume.contactInfo?.fullName || 'Unnamed'} •{' '}
                      {resume.experience?.length || 0} experiences
                    </p>
                  </div>
                  {resume.isDefault && (
                    <span className="text-xs text-primary-600 font-medium">Default</span>
                  )}
                </div>
              </div>
            ))}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="secondary" onClick={() => setShowResumeModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setShowResumeModal(false);
                  handleTailor();
                }}
                loading={tailoring || analyzingDomain}
                disabled={!selectedResumeId}
              >
                <SparklesIcon className="h-5 w-5 mr-2" />
                {analyzingDomain ? 'Analyzing...' : 'Tailor Resume'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default JobDetailPage;
